import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Activity, AlertTriangle, CheckCircle, Shield, RefreshCw, Download, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import DomainHealthPanel from './components/DomainHealthPanel';
import CorsConfigPanel from './components/CorsConfigPanel';
import SupabaseConnectivityPanel from './components/SupabaseConnectivityPanel';

export default function SystemConnectivityDashboard() {
  const { profile, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [overallHealth, setOverallHealth] = useState({
    status: 'checking',
    score: 0,
    issues: 0
  });
  const [healthData, setHealthData] = useState({
    domain: { status: 'checking', issues: [] },
    cors: { status: 'checking', issues: [] },
    supabase: { status: 'checking', issues: [] }
  });

  useEffect(() => {
    if (!isSuperAdmin?.()) {
      return;
    }
    performHealthCheck();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => {
      performHealthCheck();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const performHealthCheck = async () => {
    try {
      setRefreshing(true);
      // Trigger health checks in child components
      setLastCheckTime(new Date());
    } catch (error) {
      console.error('Error performing health check:', error);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const updateHealthData = (component, data) => {
    setHealthData(prev => ({
      ...prev,
      [component]: data
    }));
  };

  useEffect(() => {
    // Calculate overall health based on component statuses
    const statuses = [healthData?.domain?.status, healthData?.cors?.status, healthData?.supabase?.status];
    const totalIssues = (healthData?.domain?.issues?.length || 0) + 
                       (healthData?.cors?.issues?.length || 0) + 
                       (healthData?.supabase?.issues?.length || 0);
    
    const healthyCount = statuses?.filter(s => s === 'healthy')?.length;
    const warningCount = statuses?.filter(s => s === 'warning')?.length;
    const errorCount = statuses?.filter(s => s === 'error')?.length;
    
    let status = 'healthy';
    let score = 100;
    
    if (errorCount > 0) {
      status = 'error';
      score = Math.max(0, 100 - (errorCount * 40) - (warningCount * 20));
    } else if (warningCount > 0) {
      status = 'warning';
      score = Math.max(50, 100 - (warningCount * 25));
    } else if (healthyCount === 3) {
      status = 'healthy';
      score = 100;
    }
    
    setOverallHealth({ status, score, issues: totalIssues });
  }, [healthData]);

  const exportDiagnostics = () => {
    const diagnosticReport = {
      timestamp: new Date()?.toISOString(),
      overallHealth,
      components: healthData,
      environment: {
        supabaseUrl: import.meta.env?.VITE_SUPABASE_URL,
        hasAnonKey: !!import.meta.env?.VITE_SUPABASE_ANON_KEY
      }
    };
    
    const blob = new Blob([JSON.stringify(diagnosticReport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `connectivity-diagnostics-${Date.now()}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  if (!isSuperAdmin?.()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">This dashboard is only accessible to Super Admins.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Running system diagnostics...</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-50 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-5 h-5" />;
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'error': return <AlertTriangle className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5 animate-pulse" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 relative">
      <Helmet>
        <title>System Connectivity Dashboard - CRM</title>
      </Helmet>

      <ComponentIdBadge
        id="pg-system-connectivity-main"
        name="System Connectivity Dashboard"
        description="Super Admin diagnostic center for domain, CORS, and Supabase connectivity monitoring"
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 relative">
          <ComponentIdBadge
            id="hdr-connectivity-title"
            name="Connectivity Dashboard Header"
            description="Page title and system health overview"
          />
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">System Connectivity Dashboard</h1>
              <p className="text-gray-600">Real-time monitoring of domain settings, CORS configuration, and Supabase connectivity</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={performHealthCheck}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Checking...' : 'Refresh'}
              </button>
              <button
                onClick={exportDiagnostics}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Overall Health Status */}
        <div className="mb-6 relative">
          <ComponentIdBadge
            id="cmp-overall-health-card"
            name="Overall Health Status Card"
            description="Aggregated system health score and status"
          />
          <div className={`bg-white rounded-lg shadow-sm border-2 p-6 ${getStatusColor(overallHealth?.status)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${getStatusColor(overallHealth?.status)}`}>
                  {getStatusIcon(overallHealth?.status)}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-1">Overall System Health</h2>
                  <p className="text-sm text-gray-600">
                    {overallHealth?.status === 'healthy' && 'All systems operational'}
                    {overallHealth?.status === 'warning' && `${overallHealth?.issues} warning(s) detected`}
                    {overallHealth?.status === 'error' && `${overallHealth?.issues} critical issue(s) detected`}
                    {overallHealth?.status === 'checking' && 'Running diagnostics...'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-900 mb-1">{overallHealth?.score}%</div>
                <div className="text-xs text-gray-500">
                  {lastCheckTime ? `Last checked: ${lastCheckTime?.toLocaleTimeString()}` : 'Checking...'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Diagnostic Panels */}
        <div className="space-y-6 relative">
          <ComponentIdBadge
            id="cmp-diagnostic-panels"
            name="Diagnostic Panels Container"
            description="Domain, CORS, and Supabase health monitoring panels"
          />
          
          {/* Domain Configuration Panel */}
          <DomainHealthPanel 
            onHealthUpdate={(data) => updateHealthData('domain', data)}
            triggerCheck={lastCheckTime}
          />

          {/* CORS Configuration Panel */}
          <CorsConfigPanel 
            onHealthUpdate={(data) => updateHealthData('cors', data)}
            triggerCheck={lastCheckTime}
          />

          {/* Supabase Connectivity Panel */}
          <SupabaseConnectivityPanel 
            onHealthUpdate={(data) => updateHealthData('supabase', data)}
            triggerCheck={lastCheckTime}
          />
        </div>

        {/* Monitoring Settings */}
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          <ComponentIdBadge
            id="cmp-monitoring-settings"
            name="Monitoring Settings Panel"
            description="Auto-refresh and alert configuration"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-gray-600" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Automated Monitoring</h3>
                <p className="text-xs text-gray-600">Health checks run automatically every 5 minutes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                <Activity className="w-3 h-3" />
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}